import { useEffect, useMemo, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import type {
  Employee,
  EmployeeCreate,
  EmployeeUpdate,
  EmploymentType,
  Gender,
  Race,
  Religion,
  Role,
  Team,
} from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { Modal } from '../components/primitives/Modal'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { EmployeesTable } from '../components/hr/EmployeesTable'
import {
  EmployeeDirectoryCards,
  type DirectoryQuickFilter,
} from '../components/hr/EmployeeDirectoryCards'
import { RoleMultiSelect } from '../components/shared/RoleMultiSelect'
import { Collapsible } from '../components/primitives/Collapsible'
import { TeamSelectWithCreate } from '../components/primitives/TeamSelectWithCreate'
import {
  PasswordResetModal,
  type PasswordResetMode,
} from '../components/hr/PasswordResetModal'
import { flags } from '../lib/feature-flags'
import { ApiError } from '../api/client'
import {
  createEmployee,
  deactivateEmployee,
  getEmployeeDirectoryStats,
  listEmployeesPage,
  updateEmployee,
} from '../api/employees'
import { listTeams } from '../api/teams'
import { roleLabel } from '../lib/role'
import { useDebounce } from '../lib/use-debounce'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'
import { PaginationControls } from '../components/lists/PaginationControls'
import { DateField } from '../components/primitives/DateField'
import { EmployeePickerInput } from '../components/primitives/EmployeePickerInput'

const ROLE_OPTIONS: ReadonlyArray<Role> = [
  'employee',
  'manager',
  'hod',
  'hr',
  'director',
  'exec_director',
]

const RACE_OPTIONS: ReadonlyArray<{ value: Race | ''; label: string }> = [
  { value: '', label: 'Not disclosed' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'indian', label: 'Indian' },
  { value: 'malay', label: 'Malay' },
  { value: 'other', label: 'Other' },
]

const RELIGION_OPTIONS: ReadonlyArray<{ value: Religion | ''; label: string }> = [
  { value: '', label: 'Not disclosed' },
  { value: 'islam', label: 'Islam' },
  { value: 'buddhist', label: 'Buddhist' },
  { value: 'hindu', label: 'Hindu' },
  { value: 'christian', label: 'Christian' },
  { value: 'other', label: 'Other' },
]

// ── Filter state shape ─────────────────────────────────────────────────────

interface FilterArgs {
  search?: string
  team?: string
  role?: Role | ''
  active?: boolean | undefined
  // Directory quick-filter card (one at a time), translated to server params.
  employmentType?: EmploymentType
  probation?: boolean
  anniversaryMonth?: number
}

// ── Numbered-pagination fetcher (native offset) ──────────────────────────────
//
// The /employees API is already offset-based (page + limit + exact total), so
// the numbered `usePagedList` hook maps to it directly — no cursor bridge. Used
// only when the PAGINATION_NUMBERED flag is ON; otherwise the sliding-window
// fetcher above drives the list.

function makeEmployeesPagedFetcher(
  filters: FilterArgs,
): (args: PagedFetchArgs) => Promise<PagedFetchResult<Employee>> {
  return async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<Employee>> => {
    const active = filters.active === undefined ? undefined : filters.active
    const result = await listEmployeesPage({
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.team ? { team: filters.team } : {}),
      ...(filters.role ? { role: filters.role as Role } : {}),
      ...(active !== undefined ? { active } : {}),
      ...(filters.employmentType ? { employmentType: filters.employmentType } : {}),
      ...(filters.probation !== undefined ? { probation: filters.probation } : {}),
      ...(filters.anniversaryMonth != null
        ? { anniversaryMonth: filters.anniversaryMonth }
        : {}),
      page,
      limit: pageSize,
    })
    return { rows: result.data, total: result.meta.total }
  }
}

// ── Page component ─────────────────────────────────────────────────────────

export function HrEmployeesPage(): JSX.Element {
  const queryClient = useQueryClient()
  // Read filter seeds off the URL so deep-links from the home AI cards
  // (and any other surface) land on the right slice. Type via `strict: false`
  // since this route hasn't declared a validateSearch schema.
  const urlSearch = useSearch({ strict: false }) as {
    search?: string
    team?: string
    role?: string
  }
  const [search, setSearch] = useState(urlSearch.search ?? '')
  const [teamFilter, setTeamFilter] = useState<string>(urlSearch.team ?? '')
  const [roleFilter, setRoleFilter] = useState<Role | ''>(
    (urlSearch.role as Role | undefined) ?? '',
  )
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('true')
  // Directory quick-filter card (Contractual / Probationers / Anniversary).
  // Single-select — clicking the active card clears it. Gated behind the
  // employeeDirectoryEnhancements flag along with the cards themselves.
  const [quickFilter, setQuickFilter] = useState<DirectoryQuickFilter | null>(null)
  const [editing, setEditing] = useState<Employee | null>(null)
  const [creating, setCreating] = useState(false)
  const [passwordTarget, setPasswordTarget] = useState<{
    employee: Employee
    mode: PasswordResetMode
  } | null>(null)

  const debouncedSearch = useDebounce(search, 250)

  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams })

  const cardsEnabled = flags.employeeDirectoryEnhancements

  // Directory stat counts drive the filter-card values. Only fetched when the
  // feature is on. Kept fresh across list mutations via the ['employees'] key
  // prefix (invalidate() below also drops this).
  const statsQuery = useQuery({
    queryKey: ['employees', 'directory-stats'],
    queryFn: getEmployeeDirectoryStats,
    enabled: cardsEnabled,
  })

  // Translate the active quick-filter card into server query params. Only one
  // card is active at a time, so at most one of these fields is set.
  const quickFilterArgs = useMemo<
    Pick<FilterArgs, 'employmentType' | 'probation' | 'anniversaryMonth'>
  >(() => {
    if (!cardsEnabled || quickFilter === null) return {}
    switch (quickFilter) {
      case 'contractual':
        return { employmentType: 'contractual' }
      case 'probationers':
        return { probation: true }
      case 'anniversary':
        // "This month" is the viewer's current local month (1–12).
        return { anniversaryMonth: new Date().getMonth() + 1 }
    }
  }, [cardsEnabled, quickFilter])

  // Stable filter object — drives query key so the list resets on filter change
  const filterArgs = useMemo<FilterArgs>(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(teamFilter ? { team: teamFilter } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
      active:
        activeFilter === 'all' ? undefined : activeFilter === 'true',
      ...quickFilterArgs,
    }),
    [debouncedSearch, teamFilter, roleFilter, activeFilter, quickFilterArgs],
  )

  const pagedFetcher = useMemo(
    () => makeEmployeesPagedFetcher(filterArgs),
    [filterArgs],
  )
  const pagedList = usePagedList<Employee>({
    queryKey: ['employees', filterArgs],
    fetcher: pagedFetcher,
  })

  const employees = pagedList.rows
  const isLoading = pagedList.isLoading
  const totalCount = pagedList.total

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['employees'] })

  // ── Empty state copy (brand voice T28) ────────────────────────────────────

  const emptyState = (
    <div
      style={{
        padding: '40px 24px',
        textAlign: 'center',
        color: 'var(--text-3)',
      }}
    >
      <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
        No one matches those filters.
      </p>
      <p style={{ fontSize: 13 }}>
        Try broadening the search — leave records are only as good as the directory behind
        them.
      </p>
    </div>
  )

  // ── Error copy ────────────────────────────────────────────────────────────

  const errorMessage = (() => {
    const err = pagedList.error
    if (!err) return undefined
    if (err instanceof ApiError) return `Failed to load employees (${err.status})`
    if (err instanceof Error) return err.message
    return 'Failed to load employees'
  })()

  return (
    <HrAccessGuard>
      <div className="page">
      <PageHead
        eyebrow="HR"
        title="Employees"
        subtitle="Onboard, edit, and deactivate employees."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="new-employee"
              onClick={() => setCreating(true)}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: 'none',
                background: 'var(--steel-blue)',
                color: 'var(--surface)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              + New employee
            </button>
            {flags.hrBulkImport ? (
              <Link
                to="/hr/import"
                data-testid="bulk-import-link"
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontWeight: 600,
                  fontSize: 14,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 44,
                  boxSizing: 'border-box',
                }}
              >
                + Bulk import
              </Link>
            ) : null}
          </div>
        }
      />

      {cardsEnabled ? (
        <EmployeeDirectoryCards
          stats={statsQuery.data}
          active={quickFilter}
          onToggle={(key) =>
            setQuickFilter((prev) => (prev === key ? null : key))
          }
        />
      ) : null}

      <FiltersBar
        search={search}
        setSearch={setSearch}
        teamFilter={teamFilter}
        setTeamFilter={setTeamFilter}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        teams={teamsQuery.data ?? []}
      />

      <section data-testid="employees-table" aria-label="Employees">
        <EmployeesTable
          employees={employees as Employee[]}
          teams={teamsQuery.data ?? []}
          loading={isLoading}
          error={errorMessage}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: ['employees'] })}
          emptyState={emptyState}
          showJoinDate={cardsEnabled}
          onEdit={setEditing}
          onPassword={setPasswordTarget}
          footer={
            !isLoading && !errorMessage && employees.length > 0 ? (
              <PaginationControls
                page={pagedList.page}
                pageSize={pagedList.pageSize}
                total={pagedList.total}
                totalPages={pagedList.totalPages}
                pageSizeOptions={pagedList.pageSizeOptions}
                rangeStart={pagedList.rangeStart}
                rangeEnd={pagedList.rangeEnd}
                canPrev={pagedList.canPrev}
                canNext={pagedList.canNext}
                onPageChange={pagedList.setPage}
                onPageSizeChange={pagedList.setPageSize}
                isFetching={pagedList.isFetching}
              />
            ) : undefined
          }
        />
      </section>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New employee"
        maxWidth={720}
      >
        {creating ? (
          <EmployeeForm
            mode="create"
            teams={teamsQuery.data ?? []}
            onClose={() => setCreating(false)}
            onSaved={() => {
              setCreating(false)
              invalidate()
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Edit · ${editing.name}` : ''}
        maxWidth={720}
      >
        {editing ? (
          <EmployeeForm
            mode="edit"
            employee={editing}
            teams={teamsQuery.data ?? []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              invalidate()
            }}
          />
        ) : null}
      </Modal>

      {passwordTarget ? (
        <PasswordResetModal
          open
          mode={passwordTarget.mode}
          employeeId={passwordTarget.employee.id}
          employeeName={passwordTarget.employee.name}
          onClose={() => setPasswordTarget(null)}
        />
      ) : null}
      </div>
    </HrAccessGuard>
  )
}

// ── FiltersBar ─────────────────────────────────────────────────────────────

interface FiltersBarProps {
  search: string
  setSearch: (v: string) => void
  teamFilter: string
  setTeamFilter: (v: string) => void
  roleFilter: Role | ''
  setRoleFilter: (v: Role | '') => void
  activeFilter: 'all' | 'true' | 'false'
  setActiveFilter: (v: 'all' | 'true' | 'false') => void
  teams: Team[]
}

function FiltersBar({
  search,
  setSearch,
  teamFilter,
  setTeamFilter,
  roleFilter,
  setRoleFilter,
  activeFilter,
  setActiveFilter,
  teams,
}: FiltersBarProps): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 200px 200px 160px',
        gap: 10,
        marginBottom: 14,
      }}
    >
      <input
        type="search"
        placeholder="Search name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="employee-search"
        style={inputStyle()}
      />
      <select
        value={teamFilter}
        onChange={(e) => setTeamFilter(e.target.value)}
        style={inputStyle()}
      >
        <option value="">All teams</option>
        {teams.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <select
        value={roleFilter}
        onChange={(e) => setRoleFilter(e.target.value as Role | '')}
        style={inputStyle()}
      >
        <option value="">All roles</option>
        {ROLE_OPTIONS.map((r) => (
          <option key={r} value={r}>
            {roleLabel(r)}
          </option>
        ))}
      </select>
      <select
        value={activeFilter}
        onChange={(e) => setActiveFilter(e.target.value as 'all' | 'true' | 'false')}
        style={inputStyle()}
      >
        <option value="true">Active</option>
        <option value="false">Inactive</option>
        <option value="all">All</option>
      </select>
    </div>
  )
}

// ── EmployeeForm ───────────────────────────────────────────────────────────

interface EmployeeFormProps {
  mode: 'create' | 'edit'
  employee?: Employee
  teams: Team[]
  onClose: () => void
  onSaved: () => void
}

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-3)',
  margin: '8px 0 4px',
}

export function EmployeeForm({
  mode,
  employee,
  teams,
  onClose,
  onSaved,
}: EmployeeFormProps): JSX.Element {
  const queryClient = useQueryClient()
  const initial = employee

  const allEmployeesQuery = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => listEmployeesPage({ limit: 200, active: true }),
  })
  const [form, setForm] = useState({
    email: initial?.email ?? '',
    name: initial?.name ?? '',
    preferredName: initial?.preferredName ?? null,
    teamId: initial?.teamId ?? teams[0]?.id ?? '',
    roles: initial?.roles && initial.roles.length
      ? initial.roles
      : initial?.role
        ? [initial.role]
        : (['employee'] satisfies Role[]),
    jobTitle: initial?.jobTitle ?? '',
    religion: initial?.religion ?? null,
    race: (initial?.race ?? null) as Race | null,
    hometown: initial?.hometown ?? null,
    distanceKm: initial?.distanceKm ?? null,
    married: initial?.married ?? false,
    children: initial?.children ?? 0,
    yearsOfService: initial?.yearsOfService ?? 0,
    onProbation: initial?.onProbation ?? true,
    probationEndDate: initial?.probationEndDate ?? null,
    contractEndDate: initial?.contractEndDate ?? null,
    lastWorkingDay: initial?.lastWorkingDay ?? null,
    bookingWindowOverride: initial?.bookingWindowOverride ?? false,
    joinDate: initial?.joinDate ?? new Date().toISOString().slice(0, 10),
    birthDate: initial?.birthDate ?? '1995-01-01',
    gender: (initial?.gender ?? 'X') as Gender,
    employmentType: (initial?.employmentType ?? 'full_time') as EmploymentType,
    annualEntitlement: initial?.annualEntitlement ?? 14,
    isManager: initial?.isManager ?? false,
    isHod: initial?.isHod ?? false,
    active: initial?.active ?? true,
    // authUserId is set by the server on first sign-in; the HR form
    // must include the field so the EmployeeCreate type matches, but
    // it always starts null.
    authUserId: initial?.authUserId ?? null,
  })
  // Employee ID (internal_id) — shown on both create and edit (prefilled from
  // the existing value), stored 5-digit canonical, deduped server-side.
  const [internalId, setInternalId] = useState(initial?.internalId ?? '')
  // Slack email override — edit-only (creation defaults it to a copy of the
  // work email server-side). Blank when equal to the work email so the field
  // reads as "no override"; blank submits as '' which resets server-side.
  const [slackEmail, setSlackEmail] = useState(
    initial?.slackEmail && initial.slackEmail !== initial.email
      ? initial.slackEmail
      : '',
  )
  const [error, setError] = useState<string | null>(null)

  // Primary approver picker. In edit mode we seed it once the employee list
  // loads by resolving the existing primaryApproverId; `approverTouched`
  // stops the seed from clobbering an HR edit.
  const [approver, setApprover] = useState<Employee | null>(null)
  const [approverTouched, setApproverTouched] = useState(false)
  useEffect(() => {
    if (approverTouched || approver !== null) return
    const id = initial?.primaryApproverId
    if (!id) return
    const match = allEmployeesQuery.data?.data.find((e) => e.id === id)
    if (match) setApprover(match)
  }, [approverTouched, approver, initial?.primaryApproverId, allEmployeesQuery.data])

  const createMutation = useMutation({
    mutationFn: (input: EmployeeCreate) => createEmployee(input),
    onSuccess: () => onSaved(),
    onError: (err) => setError(formatError(err)),
  })
  const updateMutation = useMutation({
    mutationFn: (input: EmployeeUpdate) =>
      updateEmployee(employee!.id, input),
    onSuccess: () => onSaved(),
    onError: (err) => setError(formatError(err)),
  })
  const deactivateMutation = useMutation({
    mutationFn: () => deactivateEmployee(employee!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] })
      onSaved()
    },
    onError: (err) => setError(formatError(err)),
  })

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deactivateMutation.isPending

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setError(null)
    if (mode === 'create') {
      // Strip the two server-derived fields from the create payload —
      // the API computes years_of_service from join_date and
      // annual_entitlement from the policy table. The form keeps them
      // in state because edit mode still exposes them.
      const {
        annualEntitlement: _annualEntitlement,
        yearsOfService: _yearsOfService,
        ...createPayload
      } = form
      // primaryApproverId only collected on the HR org-chart Add Employee
      // modal; the HR Employees page form doesn't surface it (HR sets it
      // there via Edit Mode → Add approver instead), so we default null.
      createMutation.mutate({
        ...createPayload,
        // `role` (primary) stays required by EmployeeCreate; derive it from
        // the first selected role and send the full `roles` set alongside it.
        role: form.roles[0] ?? 'employee',
        internalId: internalId.trim() === '' ? null : internalId.trim(),
        primaryApproverId: approver?.id ?? null,
      })
    } else {
      // Years of service + annual entitlement are server-derived — never send
      // them from the form. Employee ID rides along (server normalizes/dedupes).
      const {
        annualEntitlement: _ae,
        yearsOfService: _yos,
        ...editPayload
      } = form
      updateMutation.mutate({
        ...editPayload,
        // Send primary `role` (legacy) + full `roles` set so both
        // pre- and post-multi-role servers update correctly.
        role: form.roles[0] ?? 'employee',
        internalId: internalId.trim() === '' ? null : internalId.trim(),
        // Slack email override rides along when the feature is on; '' tells
        // the server to reset it back to a copy of the work email.
        ...(flags.slackEmailOverride ? { slackEmail: slackEmail.trim() } : {}),
        // Only send the approver when HR actually touched the picker — an
        // untouched edit must not rewrite the existing assignment.
        ...(approverTouched ? { primaryApproverId: approver?.id ?? null } : {}),
      })
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* IDENTITY */}
      <p style={SECTION_LABEL_STYLE}>Identity</p>
      <Row>
        <Field label="Name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            style={inputStyle()}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            style={inputStyle()}
          />
        </Field>
      </Row>
      <Row>
        <Field label="Employee ID">
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g. 03548"
            value={internalId}
            onChange={(e) => setInternalId(e.target.value)}
            style={inputStyle()}
          />
        </Field>
        <Field label="Preferred name">
          <input
            type="text"
            value={form.preferredName ?? ''}
            placeholder="Leave blank to use full name"
            maxLength={100}
            onChange={(e) =>
              setForm({ ...form, preferredName: e.target.value === '' ? null : e.target.value })
            }
            style={inputStyle()}
          />
        </Field>
      </Row>
      {flags.slackEmailOverride && mode === 'edit' ? (
        <Row>
          <Field label="Slack email">
            <input
              type="email"
              value={slackEmail}
              placeholder={form.email}
              onChange={(e) => setSlackEmail(e.target.value)}
              style={inputStyle()}
            />
            <span style={hintStyle()}>Only if different from work email</span>
          </Field>
        </Row>
      ) : null}

      {/* ORGANIZATION */}
      <p style={SECTION_LABEL_STYLE}>Organization</p>
      <Row>
        <Field label="Team">
          <TeamSelectWithCreate
            value={form.teamId}
            onChange={(teamId) => setForm({ ...form, teamId })}
            testId="employee-form-team"
            required
            teams={teams}
          />
        </Field>
        <Field label="Employment type">
          <select
            value={form.employmentType}
            onChange={(e) =>
              setForm({ ...form, employmentType: e.target.value as EmploymentType })
            }
            style={inputStyle()}
          >
            <option value="full_time">Full-time</option>
            <option value="contractual">Contractual</option>
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="Job title">
          <input
            type="text"
            value={form.jobTitle}
            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
            required
            style={inputStyle()}
          />
        </Field>
        <Field label="Reports to">
          <EmployeePickerInput
            value={approver}
            onChange={(next) => {
              setApproverTouched(true)
              setApprover(next)
            }}
            testId="employee-approver"
            placeholder="Search a manager, HOD, or director…"
            clearLabel="× No approver"
            {...(employee?.id ? { excludeId: employee.id } : {})}
          />
          <span style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4, marginTop: 2 }}>
            Primary approver. Clear to leave them an orphan node.
          </span>
        </Field>
      </Row>
      {flags.multiRole ? (
        <RoleMultiSelect
          value={form.roles}
          onChange={(roles) => setForm({ ...form, roles })}
        />
      ) : (
        <Field label="Role">
          <select
            value={form.roles[0]}
            onChange={(e) => setForm({ ...form, roles: [e.target.value as Role] })}
            style={inputStyle()}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </Field>
      )}
      {/*
        The old "Is manager" / "Is HOD" checkboxes were dropped — redundant
        with the Role selector. The decorative ``isManager`` / ``isHod``
        columns ride along unchanged from the loaded row. The primary approver
        writes an ``approval_assignments`` PRIMARY row server-side.
      */}

      {/* PERSONAL */}
      <p style={SECTION_LABEL_STYLE}>Personal</p>
      <Row>
        <Field label="Join date">
          <DateField
            value={form.joinDate || null}
            onChange={(iso) => setForm({ ...form, joinDate: iso ?? '' })}
            data-testid="join-date-input"
          />
        </Field>
        <Field label="Birth date">
          <DateField
            value={form.birthDate || null}
            onChange={(iso) => setForm({ ...form, birthDate: iso ?? '' })}
            data-testid="birth-date-input"
          />
        </Field>
      </Row>
      <Row>
        <Field label="Gender">
          <select
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value as Gender })}
            style={inputStyle()}
          >
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="X">Other</option>
          </select>
        </Field>
        <Field label="On probation">
          <label style={checkRowStyle()}>
            <input
              type="checkbox"
              checked={form.onProbation}
              onChange={(e) => setForm({ ...form, onProbation: e.target.checked })}
            />
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
              Currently on probation
            </span>
          </label>
        </Field>
      </Row>

      {/* LIFECYCLE & CONTRACT — secondary, collapsed by default */}
      <Collapsible trigger="Lifecycle & contract dates (optional)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <Row>
            <Field label="Probation end date">
              <DateField
                value={form.probationEndDate ?? null}
                onChange={(iso) => setForm({ ...form, probationEndDate: iso })}
                data-testid="probation-end-date-input"
              />
            </Field>
            <Field label="Booking window override">
              <label style={checkRowStyle()}>
                <input
                  type="checkbox"
                  checked={form.bookingWindowOverride}
                  onChange={(e) =>
                    setForm({ ...form, bookingWindowOverride: e.target.checked })
                  }
                  data-testid="booking-window-override-checkbox"
                />
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  Lift advance-booking cap
                </span>
              </label>
            </Field>
          </Row>
          <Row>
            <Field label="Contract end date">
              <DateField
                value={form.contractEndDate ?? null}
                onChange={(iso) => setForm({ ...form, contractEndDate: iso })}
                data-testid="contract-end-date-input"
              />
              <span style={hintStyle()}>
                Fixed-term contracts only. Renew or switch to permanent and clear
                this when it expires.
              </span>
            </Field>
            <Field label="Last working day">
              <DateField
                value={form.lastWorkingDay ?? null}
                onChange={(iso) => setForm({ ...form, lastWorkingDay: iso })}
                data-testid="last-working-day-input"
              />
              <span style={hintStyle()}>
                Set on resignation. Annual leave pro-rates to this date. Clear if
                withdrawn.
              </span>
            </Field>
          </Row>
          {mode === 'create' ? null : (
            <Field label="Years of service">
              <div
                style={{
                  ...inputStyle(),
                  background: 'var(--surface-2)',
                  color: 'var(--text-2)',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="Calculated automatically from the join date"
              >
                {(() => {
                  const d = form.joinDate ? new Date(form.joinDate) : null
                  if (d == null || Number.isNaN(d.getTime())) return '—'
                  const yrs = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000)
                  return yrs < 0 ? '—' : `${yrs.toFixed(1)} years`
                })()}
              </div>
            </Field>
          )}
        </div>
      </Collapsible>

      {/* DEMOGRAPHICS — used for holiday observance; collapsed by default */}
      <Collapsible trigger="Demographics (optional)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <Row>
            <Field label="Race">
              <select
                value={form.race ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setForm({ ...form, race: v === '' ? null : (v as Race) })
                }}
                style={inputStyle()}
              >
                {RACE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Religion">
              <select
                value={form.religion ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    religion: e.target.value === '' ? null : (e.target.value as Religion),
                  })
                }
                style={inputStyle()}
              >
                <option value="">— Select —</option>
                {RELIGION_OPTIONS.filter((opt) => opt.value !== '').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Hometown">
              <input
                type="text"
                value={form.hometown ?? ''}
                onChange={(e) =>
                  setForm({ ...form, hometown: e.target.value === '' ? null : e.target.value })
                }
                style={inputStyle()}
              />
            </Field>
            <Field label="Distance from home (km)">
              <input
                type="number"
                min={0}
                value={form.distanceKm ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    distanceKm: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                style={inputStyle()}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Married">
              <label style={checkRowStyle()}>
                <input
                  type="checkbox"
                  checked={form.married}
                  onChange={(e) => setForm({ ...form, married: e.target.checked })}
                />
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Married</span>
              </label>
            </Field>
            <Field label="Children">
              <input
                type="number"
                min={0}
                value={form.children}
                onChange={(e) => setForm({ ...form, children: Number(e.target.value) })}
                style={inputStyle()}
              />
            </Field>
          </Row>
        </div>
      </Collapsible>

      {error ? (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 }}>
        <div>
          {mode === 'edit' && employee?.active ? (
            <button
              type="button"
              data-testid="deactivate-employee"
              disabled={isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Deactivate ${employee.name}? They will no longer appear by default in the directory.`,
                  )
                )
                  deactivateMutation.mutate()
              }}
              style={dangerGhostButton()}
            >
              Deactivate
            </button>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            style={ghostButton()}
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="save-employee"
            disabled={isPending}
            style={primaryButton(isPending)}
          >
            {isPending
              ? 'Saving…'
              : mode === 'create'
                ? 'Create'
                : 'Save changes'}
          </button>
        </div>
      </div>
    </form>
  )
}

// ── Small layout components ────────────────────────────────────────────────

function Row({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{label}</span>
      {children}
    </label>
  )
}

// ── Style helpers ──────────────────────────────────────────────────────────

function inputStyle(): React.CSSProperties {
  return {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--surface)',
    fontSize: 13,
    fontFamily: 'inherit',
  }
}

/** A checkbox + label row sized to line up with sibling inputs in a grid. */
function checkRowStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 38,
    cursor: 'pointer',
  }
}

/** Sub-field helper text. */
function hintStyle(): React.CSSProperties {
  return {
    fontSize: 11,
    color: 'var(--text-3)',
    lineHeight: 1.4,
    marginTop: 2,
  }
}

function primaryButton(disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    background: disabled ? 'var(--border-strong)' : 'var(--steel-blue)',
    color: 'var(--surface)',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }
}

function ghostButton(): React.CSSProperties {
  return {
    padding: '6px 12px',
    background: 'var(--surface)',
    color: 'var(--text-2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
  }
}

function dangerGhostButton(): React.CSSProperties {
  return {
    padding: '8px 14px',
    background: 'var(--surface)',
    color: 'var(--danger-fg)',
    border: '1px solid var(--danger-line)',
    borderRadius: 8,
    fontWeight: 600,
    cursor: 'pointer',
  }
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Operation failed'
}
